import type { PtlinkedInitConfig } from '@mdvip/shared/features/workout-library/types';
import { useQuery } from '@tanstack/react-query';
import type { FitnessWidgetConfig } from './fitnessWidgetConfigApi';
import { requestFitnessWidgetConfig } from './fitnessWidgetConfigApi';

export const fitnessWidgetConfigKey = ['resources', 'fitnessWidgetConfig'] as const;

/**
 * Maps the backend's `myFitnessWidgetConfig` (camelCase GraphQL contract) onto the vendor's
 * `PtlinkedInitConfig` (snake_case, passed straight through to `$(container).ptlinkedLibrary()`
 * — see types.ts). Two different contracts on purpose: ours is our own API to keep idiomatic;
 * the vendor's is not ours to redesign.
 *
 * `app_root_url` has no backend equivalent — it's the URL of the page the widget is mounted on,
 * something only the client knows, so it's resolved here rather than requested.
 *
 * `user_type` is typed as a plain `String!` on the wire (the resolver only implements the
 * member → "patient" mapping today; provider/staff throw server-side — see CNP-640). This cast
 * narrows to the vendor's declared union rather than re-validating a value the backend already
 * guarantees is one of these four strings.
 */
function toPtlinkedInitConfig(config: FitnessWidgetConfig): PtlinkedInitConfig {
  return {
    api_key: config.apiKey,
    api_root_url: config.apiRootUrl,
    app_root_url: window.location.href,
    user_uid: config.userUid,
    user_type: config.userType as PtlinkedInitConfig['user_type'],
    header_element_class: config.headerElementClass,
    save_favorites: config.saveFavorites,
    secure_messaging: config.secureMessaging,
    training_mode: config.trainingMode,
    debug_mode: config.debugMode,
    dialog_box_type: config.dialogBoxType,
    printed_handouts_url: config.printedHandoutsUrl,
    masquerade: config.masquerade,
  };
}

/** The member's PTLINKED exercise-library init config, backed by `myFitnessWidgetConfig`. */
export function useWorkoutLibraryConfig() {
  return useQuery({
    queryKey: fitnessWidgetConfigKey,
    queryFn: requestFitnessWidgetConfig,
    select: toPtlinkedInitConfig,
  });
}
